import { addPart, setMaterial, type ModelPart } from '../../lib/model-doc.js';
import { useModel } from '../../state/model.js';
import type { IconName } from '../ui/icons.js';
import { MATERIAL_PRESETS } from './material-presets.js';

/**
 * Add part → Building blocks: ready-made multi-part pieces for hard-surface
 * models (vehicles, props). Sized for a car-sized model (meters, -Z forward)
 * and placed where such a piece usually sits; every part stays an ordinary
 * part afterwards. A block adds the preset materials it uses when the model
 * has no material of that name yet.
 */
export interface PartBlock {
  id: string;
  label: string;
  icon: IconName;
  /** Material name → material preset id. */
  materials: Record<string, string>;
  part: () => ModelPart;
}

const Z: [number, number, number] = [90, 0, 0];
const AXLE_X: [number, number, number] = [0, 0, 90];

export const PART_BLOCKS: readonly PartBlock[] = [
  {
    id: 'wheel',
    label: 'Wheel (tyre, rim, spokes, glow)',
    icon: 'smooth',
    materials: { rubber: 'rubber', carbon: 'carbon', chrome: 'chrome', neon_cyan: 'neon-cyan' },
    part: () => ({
      name: 'wheel',
      transform: { position: [0.84, 0.35, -1.35] },
      children: [
        { name: 'tyre', material: 'rubber', shape: { type: 'torus', radius: 0.25, tube: 0.1, segments: 40, sides: 16 }, transform: { rotation: AXLE_X } },
        { name: 'rim', material: 'carbon', shape: { type: 'cylinder', radius: 0.2, height: 0.19, segments: 40 }, transform: { rotation: AXLE_X } },
        {
          name: 'spoke',
          material: 'chrome',
          shape: { type: 'box', size: [0.04, 0.17, 0.05], bevel: 0.01 },
          transform: { position: [0.085, 0, 0] },
          array: { count: 1, radial: { count: 6, axis: 'x', radius: 0.1 } },
        },
        { name: 'hub', material: 'chrome', shape: { type: 'cylinder', radius: 0.055, height: 0.22, segments: 24 }, transform: { rotation: AXLE_X } },
        { name: 'glow', material: 'neon_cyan', shape: { type: 'torus', radius: 0.2, tube: 0.012, segments: 48, sides: 8 }, transform: { position: [0.1, 0, 0], rotation: AXLE_X } },
      ],
    }),
  },
  {
    id: 'body',
    label: 'Body shell (rounded, front to back)',
    icon: 'circle',
    materials: { paint: 'anime-paint' },
    part: () => ({
      name: 'body',
      material: 'paint',
      shape: {
        type: 'loft',
        segments: 32,
        sections: [
          { position: [0, 0.5, -2.2], rotation: Z, rect: [1.5, 0.28, 0.12] },
          { position: [0, 0.58, -1.6], rotation: Z, rect: [1.85, 0.46, 0.2] },
          { position: [0, 0.66, 0], rotation: Z, rect: [1.9, 0.6, 0.24] },
          { position: [0, 0.7, 1.6], rotation: Z, rect: [1.9, 0.6, 0.24] },
          { position: [0, 0.7, 2.2], rotation: Z, rect: [1.7, 0.44, 0.16] },
        ],
      },
    }),
  },
  {
    id: 'canopy',
    label: 'Canopy / cabin glass',
    icon: 'circle',
    materials: { glass: 'glass' },
    part: () => ({
      name: 'canopy',
      material: 'glass',
      shape: {
        type: 'loft',
        segments: 32,
        sections: [
          { position: [0, 0.95, -1.0], rotation: Z, rect: [1.36, 0.04, 0.02] },
          { position: [0, 1.12, -0.35], rotation: Z, rect: [1.34, 0.4, 0.16] },
          { position: [0, 1.12, 0.5], rotation: Z, rect: [1.3, 0.4, 0.16] },
          { position: [0, 0.98, 1.35], rotation: Z, rect: [1.1, 0.05, 0.02] },
        ],
      },
    }),
  },
  {
    id: 'wing',
    label: 'Rear wing (blade, struts, end plates)',
    icon: 'layers',
    materials: { carbon: 'carbon' },
    part: () => ({
      name: 'wing',
      transform: { position: [0, 1.15, 2.0] },
      children: [
        { name: 'blade', material: 'carbon', shape: { type: 'box', size: [1.75, 0.05, 0.36], bevel: 0.015 }, transform: { rotation: [-8, 0, 0] } },
        { name: 'end_plate', material: 'carbon', mirror: 'x', shape: { type: 'box', size: [0.035, 0.22, 0.44], bevel: 0.01 }, transform: { position: [0.875, 0, 0] } },
        { name: 'strut', material: 'carbon', mirror: 'x', shape: { type: 'box', size: [0.05, 0.3, 0.1], bevel: 0.01 }, transform: { position: [0.42, -0.17, 0.02] } },
      ],
    }),
  },
  {
    id: 'lamps',
    label: 'Lamp pair (mirrored)',
    icon: 'sparkles',
    materials: { headlight: 'headlight' },
    part: () => ({
      name: 'headlight',
      material: 'headlight',
      mirror: 'x',
      shape: { type: 'box', size: [0.42, 0.06, 0.08], bevel: 0.02 },
      transform: { position: [0.62, 0.66, -2.05], rotation: [0, -14, 0] },
    }),
  },
  {
    id: 'strip',
    label: 'Light strip',
    icon: 'sparkles',
    materials: { neon_cyan: 'neon-cyan' },
    part: () => ({ name: 'light_strip', material: 'neon_cyan', shape: { type: 'box', size: [1.4, 0.04, 0.04], bevel: 0.01 } }),
  },
  {
    id: 'exhaust',
    label: 'Exhaust pair',
    icon: 'circle',
    materials: { chrome: 'chrome' },
    part: () => ({
      name: 'exhaust',
      material: 'chrome',
      mirror: 'x',
      shape: { type: 'cylinder', radius: 0.055, height: 0.22, segments: 24 },
      transform: { position: [0.45, 0.42, 2.25], rotation: Z },
    }),
  },
];

/** Adds `block` (and the materials it needs) to the open model, selected. */
export function addBlock(block: PartBlock, asChild: boolean): void {
  const s = useModel.getState();
  let doc = s.doc;
  if (!doc) return;
  for (const [name, presetId] of Object.entries(block.materials)) {
    if (doc.materials?.[name]) continue;
    const preset = MATERIAL_PRESETS.find((p) => p.id === presetId);
    if (preset) doc = setMaterial(doc, name, structuredClone(preset.material));
  }
  const result = addPart(doc, asChild ? s.selectedIndex() : null, block.part());
  if (result.path.length) s.update(result.doc, result.path);
}
